import CandidateRating from "../components/CandidateRating";
import { authenticationFingerprint } from "../services/authSession";
import { loadCandidateList } from "../services/candidateList";
import { readCandidateListCache } from "../services/candidateListCache";
import RqthBadge from "../components/RqthBadge";
import "./CandidatsPage.css";
import { candidateCompetenceCatalogs } from "../data/candidateCompetenceCatalogs";
import { regionArray } from "../utils/geographicSectors";
import crmQualifications from "../utils/qualifications";
import {
  candidateJobs,
  matchesCandidateFilters,
} from "../utils/candidateFilters";
import useSharedRecord from "../hooks/useSharedRecord";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { formatPhone } from "../utils/formatPhone";
import { candidateName } from "../utils/candidateName";
import { downloadCandidates } from "../utils/downloadCandidates";
import PageHeading from "../components/PageHeading";
import { createListSearch, normalizeSearch } from "../utils/listSearch";
import { apiErrorMessage } from "../utils/apiError";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  UserPlus,
  Search,
  Loader2,
  Mail,
  Phone,
  FileText,
  Download,
  Clock,
  SlidersHorizontal,
} from "lucide-react";
import axios from "../axios/AxiosInstance";
import { message, Modal, Select } from "antd";
import CreateCandidateModal from "./candidate/CreateCandidateModal";
import { CandidateModal } from "./candidate/CandidateModal";

export default function CandidatsPage() {
  const [recordsReady, setRecordsReady] = useState(() => readCandidateListCache() !== null);
  const [originalCandidates, setOriginalCandidates] = useState(() => readCandidateListCache() || []);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isAddCandidate, setIsAddCandidate] = useState(false);
  const [isViewCandidate, setIsViewCandidate] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { select: selectSharedCandidate, missing: missingSharedCandidate } =
    useSharedRecord(originalCandidates, recordsReady, setSelectedCandidate);
  useEffect(() => {
    setIsViewCandidate(Boolean(selectedCandidate));
  }, [selectedCandidate]);

  const [cv, setCv] = useState(null);
  const [cvBusy, setCvBusy] = useState(null);
  useEffect(
    () => () => {
      if (cv?.url) URL.revokeObjectURL(cv.url);
    },
    [cv],
  );
  const viewCv = async (candidate) => {
    if (cvBusy) return;
    setCvBusy(candidate._id);
    try {
      const { data } = await axios.get(`cv/${candidate._id}`);
      if (!data?.data) throw new Error();
      const raw = atob(data.data.replace(/^data:[^,]+,/, ""));
      const blob = new Blob(
        [Uint8Array.from(raw, (char) => char.charCodeAt(0))],
        { type: "application/pdf" },
      );
      setCv({ name: candidateName(candidate), url: URL.createObjectURL(blob) });
    } catch (error) {
      message.error(
        apiErrorMessage(error, "CV indisponible pour ce candidat."),
      );
    } finally {
      setCvBusy(null);
    }
  };
  const [exportBusy, setExportBusy] = useState(false);
  // Nouveaux états pour le design moderne
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, searching] = useDebouncedValue(searchQuery, 150);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState("tous");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [competences, setCompetences] = useState({
    diplomas: [],
    habilitations: [],
    caceses: [],
    licences: [],
  });
  const filterCount =
    Number(jobs.length > 0) +
    Number(sectors.length > 0) +
    Object.values(competences).filter((values) => values.length).length;
  const jobOptions = crmQualifications.map((label) => ({
    label,
    value: normalizeSearch(label),
  }));
  const sectorOptions = regionArray.map((label) => ({ label, value: label }));
  const resetFilters = () => {
    setJobs([]);
    setSectors([]);
    setCompetences({
      diplomas: [],
      habilitations: [],
      caceses: [],
      licences: [],
    });
    setStatusFilter("tous");
    setSearchQuery("");
  };

  // Statistiques des candidats
  const [stats, setStats] = useState({
    total: originalCandidates.length,
    enMission: originalCandidates.filter(candidate => candidate.inMissionToday === true).length,
  });

  // Fonction pour calculer les statistiques
  const calculateStats = (candidatesList) => {
    const total = candidatesList.length;
    const enMission = candidatesList.filter(
      (c) => c.inMissionToday === true,
    ).length;

    setStats({ total, enMission });
  };

  const search = setSearchQuery;
  const filterByStatus = setStatusFilter;
  const candidateSearch = useMemo(() => createListSearch(appliedQuery, [
    "firstname",
    "lastname",
    "phone",
    "mobile",
    "email",
    "address",
    "town",
    "postCode",
    "qualification",
    "qualifications",
    "qualifications.name",
    "qualifications.label",
    "locations",
    "locations.town",
    "status",
    "pld",
  ]), [appliedQuery]);
  const candidates = useMemo(() => originalCandidates.filter(
    (candidate) =>
      (statusFilter === "tous" ||
        (statusFilter === "En mission"
          ? candidate.inMissionToday === true
          : normalizeSearch(candidate.status) ===
            normalizeSearch(statusFilter))) &&
      candidateSearch(candidate) &&
      matchesCandidateFilters(candidate, { jobs, sectors, ...competences }),
  ), [originalCandidates, statusFilter, candidateSearch, jobs, sectors, competences]);
  useEffect(() => { setPage(1); }, [appliedQuery, statusFilter, jobs, sectors, competences, pageSize]);
  const pageCount = Math.max(1, Math.ceil(candidates.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visibleCandidates = candidates.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    const controller = new AbortController();
    const session = authenticationFingerprint();
    setIsLoading(readCandidateListCache() === null);
    setLoadError("");
    loadCandidateList()
      .then((data) => {
        if (controller.signal.aborted || session !== authenticationFingerprint()) return;
        setOriginalCandidates(data);
        setRecordsReady(true);
        calculateStats(data);
      })
      .catch((error) => {
        if (controller.signal.aborted || session !== authenticationFingerprint()) return;
        const detail = apiErrorMessage(error, "Une erreur s'est produite !");
        if (detail) {
          setLoadError(detail);
          message.error(detail);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, []);

  const viewCandidate = (candidate) => {
    selectSharedCandidate(candidate);
  };

  // Fonction pour obtenir l'icône de statut

  const exportCandidates = async () => {
    if (exportBusy || searching || !candidates.length) return;
    setExportBusy(true);
    try {
      downloadCandidates(
        candidates,
        searchQuery ||
          (filterCount || statusFilter !== "tous" ? "filtres" : "tous"),
      );
      message.success(`${candidates.length} candidat(s) exporté(s).`);
    } catch (error) {
      message.error(
        apiErrorMessage(
          error,
          "Impossible d’exporter les candidats. Réessayez.",
        ),
      );
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header Section */}
        <motion.div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div>
                <PageHeading
                  icon="candidates"
                  title="Candidats"
                  description={
                    <>
                      {" "}
                      Gérez votre vivier de talents et identifiez les meilleurs
                      profils{" "}
                    </>
                  }
                />
              </div>
            </div>

            <motion.button
              onClick={() => setIsAddCandidate(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <UserPlus className="w-5 h-5" />
              <span>Nouveau candidat</span>
            </motion.button>
          </div>
        </motion.div>

        {cv && (
          <Modal
            open
            title={cv ? `CV — ${cv.name}` : "CV"}
            onCancel={() => setCv(null)}
            footer={null}
            width={900}
          >
            {cv && (
              <iframe
                src={cv.url}
                title={`CV de ${cv.name}`}
                className="h-[70vh] w-full border-0"
              />
            )}
          </Modal>
        )}
        {/* Modals */}
        {isAddCandidate && (
          <CreateCandidateModal
            open={isAddCandidate}
            candidates={originalCandidates}
            setCandidates={setOriginalCandidates}
            onCreated={(candidate) => {
              setSelectedCandidate(candidate);
              setIsViewCandidate(true);
            }}
            onOpenChange={setIsAddCandidate}
          />
        )}
        {isViewCandidate && selectedCandidate && (
          <CandidateModal
            key={selectedCandidate?._id}
            isOpen={isViewCandidate}
            onClose={() => selectSharedCandidate(null)}
            candidates={originalCandidates}
            setCandidates={setOriginalCandidates}
            candidate={selectedCandidate}
          />
        )}

        {loadError && (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700"
          >
            {loadError}
          </p>
        )}
        {missingSharedCandidate && (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
          >
            Cette fiche candidat est introuvable ou inaccessible dans votre
            agence.
          </p>
        )}
        {/* Statistiques */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total candidats</p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.total}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p
                  className="text-sm text-slate-600"
                  title="Candidats en intérim, CDD ou vacation aujourd’hui, comptés une seule fois"
                >
                  En mission aujourd’hui
                </p>
                <p className="text-3xl font-bold text-orange-600">
                  {stats.enMission}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Barre d'outils moderne */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Recherche */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <motion.input
                type="search"
                onChange={(e) => search(e.target.value)}
                value={searchQuery}
                placeholder="Nom, téléphone, email, ville, qualification…"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                whileFocus={{ scale: 1.02 }}
              />
            </div>

            <button
              type="button"
              disabled={
                exportBusy || isLoading || searching || !candidates.length
              }
              onClick={exportCandidates}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-40"
            >
              <Download size={17} />
              {exportBusy
                ? "Export en cours…"
                : `Exporter en Excel (${candidates.length})`}
            </button>
            <button
              type="button"
              aria-expanded={filtersOpen}
              aria-controls="candidate-filters"
              onClick={() => setFiltersOpen((value) => !value)}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <SlidersHorizontal size={17} />
              Filtres{filterCount > 0 ? ` (${filterCount})` : ""}
            </button>
            {/* Filtres */}
            <div className="flex items-center space-x-3">
              <motion.select
                value={statusFilter}
                onChange={(e) => filterByStatus(e.target.value)}
                className="px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                whileFocus={{ scale: 1.02 }}
              >
                <option value="tous">Tous les statuts</option>
                <option value="Disponible">Disponible</option>
                <option value="En mission">En mission</option>
                <option value="Indisponible">Indisponible</option>
              </motion.select>
            </div>
          </div>
          {filtersOpen && (
            <div
              id="candidate-filters"
              className="mt-5 grid gap-4 border-t border-slate-100 pt-5 md:grid-cols-3"
            >
              <div className="space-y-2">
                <label
                  htmlFor="candidate-jobs"
                  className="block text-sm font-medium text-slate-600"
                >
                  Postes
                </label>
                <Select
                  id="candidate-jobs"
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Tous les postes"
                  value={jobs}
                  options={jobOptions}
                  onChange={setJobs}
                  filterOption={(input, option) =>
                    normalizeSearch(option.label).includes(
                      normalizeSearch(input),
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="candidate-sectors"
                  className="block text-sm font-medium text-slate-600"
                >
                  Secteur géographique
                </label>
                <Select
                  id="candidate-sectors"
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Tous les secteurs"
                  value={sectors}
                  options={sectorOptions}
                  onChange={setSectors}
                  filterOption={(input, option) =>
                    normalizeSearch(option.label).includes(
                      normalizeSearch(input),
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
              {[
                ["diplomas", "Diplômes"],
                ["habilitations", "Habilitations"],
                ["caceses", "CACES"],
                ["licences", "Permis de conduire"],
              ].map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <label
                    htmlFor={`candidate-${key}`}
                    className="block text-sm font-medium text-slate-600"
                  >
                    {label}
                  </label>
                  <Select
                    id={`candidate-${key}`}
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Tous"
                    value={competences[key]}
                    options={candidateCompetenceCatalogs[key].map((label) => ({
                      label,
                      value: normalizeSearch(label),
                    }))}
                    onChange={(values) =>
                      setCompetences((current) => ({
                        ...current,
                        [key]: values,
                      }))
                    }
                    filterOption={(input, option) =>
                      normalizeSearch(option.label).includes(
                        normalizeSearch(input),
                      )
                    }
                    style={{ width: "100%" }}
                  />
                </div>
              ))}
            </div>
          )}
          {(filterCount > 0 || statusFilter !== "tous" || searchQuery) && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <p aria-live="polite" className="text-slate-500">
                {candidates.length} candidat{candidates.length > 1 ? "s" : ""}{" "}
                sur {originalCandidates.length}
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="font-medium text-slate-600 underline underline-offset-4"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </motion.div>

        {/* Affichage des candidats */}
        {isLoading ? (
          <motion.div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600">Chargement des candidats...</p>
            </div>
          </motion.div>
        ) : (
          <motion.div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table data-pagination="none" aria-busy={searching} className="candidateDirectoryTable w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Candidat
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Contact
                      </th>

                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Compétences
                      </th>
                      <th scope="col">Note moyenne</th>
                      <th scope="col" style={{ width: "64px" }}>
                        CV
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                      {visibleCandidates.map((candidat) => (
                        <tr
                          key={candidat._id}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => viewCandidate(candidat)}
                          tabIndex={0}
                          aria-label={`Ouvrir la fiche de ${candidat.firstname} ${candidat.lastname}`}
                          onKeyDown={(e) => {
                            if (
                              e.target === e.currentTarget &&
                              ["Enter", " "].includes(e.key)
                            ) {
                              e.preventDefault();
                              viewCandidate(candidat);
                            }
                          }}
                        >
                          <td className="px-6 py-4">
                            <div className="candidateDirectoryIdentity">
                              <div className="relative">
                                {candidat.image ? (
                                  <img
                                    src={`data:image/jpeg;base64,${candidat.image}`}
                                    alt={candidateName(candidat)}
                                    className="candidateDirectoryAvatar object-cover"
                                  />
                                ) : (
                                  <div className="candidateDirectoryAvatar">
                                    {candidat.firstname?.charAt(0)}
                                    {candidat.lastname?.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">
                                  {candidateName(candidat)}
                                  <RqthBadge value={candidat.isRQTH} />
                                </p>
                                {candidat.pld && (
                                  <p className="candidateDirectoryReference">
                                    N° {candidat.pld}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                <Mail className="w-3 h-3" />
                                <span className="truncate max-w-48">
                                  {candidat.email}
                                </span>
                              </div>
                              {candidat.phone && (
                                <div className="flex items-center space-x-2 text-sm text-slate-600">
                                  <Phone className="w-3 h-3" />
                                  <span>{formatPhone(candidat.phone)}</span>
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="candidateDirectorySkills">
                              {!candidateJobs(candidat).length && (
                                <span className="text-xs text-slate-400">
                                  Non renseignées
                                </span>
                              )}
                              {candidateJobs(candidat)
                                .slice(0, 2)
                                .map((comp, index) => (
                                  <span
                                    key={index}
                                    className="candidateDirectorySkill"
                                  >
                                    {comp}
                                  </span>
                                ))}
                              {candidateJobs(candidat).length > 2 && (
                                <span
                                  title={candidateJobs(candidat)
                                    .slice(2)
                                    .join(", ")}
                                  className="candidateDirectorySkill"
                                >
                                  +{candidateJobs(candidat).length - 2}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="align-middle">
                            <CandidateRating summary={candidat.ratingSummary} />
                          </td>
                          <td className="text-center align-middle">
                            <button
                              type="button"
                              title="Voir le CV"
                              aria-label={`Voir le CV de ${candidat.firstname} ${candidat.lastname}`}
                              disabled={Boolean(cvBusy)}
                              className="candidateDirectoryCv"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewCv(candidat);
                              }}
                            >
                              {cvBusy === candidat._id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {candidates.length > 0 && (
              <nav aria-label="Pagination des candidats" className="table-pagination">
                <span aria-live="polite">{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, candidates.length)} sur {candidates.length}</span>
                <label>Lignes par page <select value={pageSize} onChange={event => setPageSize(Number(event.target.value))}>{[25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}</select></label>
                <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Précédent</button>
                <span>Page {currentPage} / {pageCount}</span>
                <button type="button" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>Suivant</button>
              </nav>
            )}

            {/* Message si aucun candidat */}
            {candidates.length === 0 && !isLoading && (
              <motion.div className="text-center py-20">
                <div className="text-slate-400 mb-4">
                  <Users className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Aucun candidat trouvé
                </h3>
                <p className="text-slate-600 mb-6">
                  {searchQuery
                    ? "Aucun candidat ne correspond à votre recherche."
                    : "Commencez par ajouter votre premier candidat."}
                </p>
                {!searchQuery && (
                  <motion.button
                    onClick={() => setIsAddCandidate(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Ajouter un candidat
                  </motion.button>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
